import React, { useRef, useMemo, useLayoutEffect } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { makeFacade } from "../lib/textures";
import { envState } from "./env";
import { useStore } from "../store";
import { POS } from "./layout";

function seeded(seed) {
  let s = seed;
  return () => {
    s = (s * 1664525 + 1013904223) % 4294967296;
    return s / 4294967296;
  };
}

// Background Lower-Manhattan skyline as two instanced meshes (LOD by device).
export default function City() {
  const { gl } = useThree();
  const lowPerf = useMemo(
    () => window.innerWidth < 820 || (gl.capabilities && gl.capabilities.maxTextureSize < 4096),
    [gl]
  );
  const modernTex = useMemo(() => makeFacade("modern"), []);
  const stoneTex = useMemo(() => makeFacade("stone"), []);
  const modernRef = useRef();
  const stoneRef = useRef();
  const modernMat = useRef();
  const stoneMat = useRef();

  const { modern, stone } = useMemo(() => {
    const rnd = seeded(9281);
    const modern = [];
    const stone = [];
    const target = lowPerf ? 320 : 800; // instanced blocks scattered across the Manhattan grid
    let attempts = 0;
    // Keep the WTC superblock (2001 plaza / 2026 pools) completely clear:
    // 100-unit radial clearance around the North/South memorial pool footprints
    // plus a rectangular core covering the whole 16-acre site.
    const inSuperblock = (x, z) => {
      const dN = (x - POS.northPool[0]) ** 2 + (z - POS.northPool[2]) ** 2;
      const dS = (x - POS.southPool[0]) ** 2 + (z - POS.southPool[2]) ** 2;
      if (dN < 10000 || dS < 10000) return true; // 100u radial clearance
      return Math.abs(x + 30) < 200 && Math.abs(z + 30) < 210; // rectangular site core
    };
    const placed = [];
    const inMidtown = (x, z) => x > -320 && x < 320 && z > -1420 && z < -820;
    // Hard-coded river / harbour no-spawn mask (Hudson, East River, Upper Bay).
    const inWater = (x, z) =>
      (x > -900 && x < -360 && z > -500 && z < 1000) ||
      (x > 470 && x < 1150 && z > -1000 && z < 1000) ||
      (x < 470 && z > 460);
    while (modern.length + stone.length < target && attempts < target * 10) {
      attempts++;
      const roll = rnd();
      let x, z, h;
      if (roll < 0.5) {
        // Financial District: tall towers ringing the site (rivers kept open)
        const ang = rnd() * Math.PI * 2;
        const rad = 220 + rnd() * 520;
        x = Math.cos(ang) * rad;
        z = Math.sin(ang) * rad - 40;
        if (inSuperblock(x, z)) continue;
        if (x < -360 || x > 470) continue; // Hudson / East River corridors
        if (x > 300 && (Math.abs(z - 150) < 85 || Math.abs(z + 248) < 85)) continue; // bridge landings
        if (z > 460) continue; // harbour (toward Liberty Island)
        const near = rad < 460;
        h = (near ? 110 : 60) + rnd() * (near ? 175 : 95); // tallest downtown cluster
      } else if (roll < 0.66) {
        // Midtown cluster to the north — the other tall skyscraper grouping
        x = -320 + rnd() * 640;
        z = -1420 + rnd() * 600;
        h = 150 + rnd() * 205;
      } else {
        // Outer horizon (Jersey City west, Brooklyn east, uptown) — shorter, fills the skyline
        const ang = rnd() * Math.PI * 2;
        const rad = 760 + rnd() * 940;
        x = Math.cos(ang) * rad;
        z = Math.sin(ang) * rad - 40;
        if (inSuperblock(x, z) || inMidtown(x, z)) continue;
        h = 40 + rnd() * 105;
      }
      const w = 20 + rnd() * 46;
      const d = 20 + rnd() * 46;
      if (inWater(x, z)) continue; // strict: no buildings in the rivers/harbour
      if (inSuperblock(x, z)) continue; // never obscure the 16-acre WTC memorial site
      // 15-unit proximity check: prevent buildings clumping / overlapping.
      let tooClose = false;
      for (let j = 0; j < placed.length; j++) {
        if ((placed[j][0] - x) ** 2 + (placed[j][1] - z) ** 2 < 225) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;
      placed.push([x, z]);
      const rot = (rnd() - 0.5) * 0.6;
      const isModern = rnd() > 0.45;
      (isModern ? modern : stone).push({ x, z, h, w, d, rot });
    }
    return { modern, stone };
  }, [lowPerf]);

  useLayoutEffect(() => {
    const build = (ref, arr) => {
      if (!ref.current) return;
      const m = new THREE.Matrix4();
      arr.forEach((b, i) => {
        m.makeRotationY(b.rot);
        m.setPosition(b.x, b.h / 2, b.z);
        m.scale(new THREE.Vector3(b.w, b.h, b.d));
        // recompose properly
        const q = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), b.rot);
        m.compose(
          new THREE.Vector3(b.x, b.h / 2, b.z),
          q,
          new THREE.Vector3(b.w, b.h, b.d)
        );
        ref.current.setMatrixAt(i, m);
      });
      ref.current.instanceMatrix.needsUpdate = true;
    };
    build(modernRef, modern);
    build(stoneRef, stone);
  }, [modern, stone]);

  useFrame(() => {
    const e = envState.night;
    if (modernMat.current) modernMat.current.emissiveIntensity = e * 1.4;
    if (stoneMat.current) stoneMat.current.emissiveIntensity = e * 1.1;
    // Perf-guard LOD: trim the number of drawn blocks on weak devices.
    const q = THREE.MathUtils.clamp(useStore.getState().qualityScale, 0.55, 1);
    if (modernRef.current) modernRef.current.count = Math.max(1, Math.floor(modern.length * q));
    if (stoneRef.current) stoneRef.current.count = Math.max(1, Math.floor(stone.length * q));
  });

  return (
    <group>
      <instancedMesh ref={modernRef} args={[null, null, modern.length]} receiveShadow frustumCulled={false}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial
          ref={modernMat}
          map={modernTex.map}
          emissiveMap={modernTex.emissiveMap}
          emissive={"#ffdca0"}
          emissiveIntensity={0}
          color="#9fb0c0"
          roughness={0.6}
          metalness={0.12}
        />
      </instancedMesh>
      <instancedMesh ref={stoneRef} args={[null, null, stone.length]} receiveShadow frustumCulled={false}>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial
          ref={stoneMat}
          map={stoneTex.map}
          emissiveMap={stoneTex.emissiveMap}
          emissive={"#ffdca0"}
          emissiveIntensity={0}
          color="#b9ab95"
          roughness={0.8}
          metalness={0.05}
        />
      </instancedMesh>
    </group>
  );
}
